// Итог завершённой тренировки для персонажа: полученный опыт, уровень (с отметкой нового) и фигура
// с мышцами тренировки — основные, вспомогательные и не работавшие. Офлайн или ошибка — карточки нет.

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useBodySex } from '@/api/settings'
import { MuscleLegend, MuscleMap } from '@/components/muscle-map/MuscleMap'
import { FIGURE_MUSCLES, type MuscleTone } from '@/components/muscle-map/muscles'
import { Badge, Card } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatNumber, pluralRu } from '@/lib/format'
import { useMuscleLabels } from '@/pages/workouts/api'

import { useSessionGain } from './api'
import { LevelCelebration } from './LevelCelebration'
import { useSyncQuests } from './quests'

const SETS = ['подход', 'подхода', 'подходов'] as const

export function SessionGainCard({ sessionId, className, celebrate = false }: { sessionId: string; className?: string; celebrate?: boolean }) {
  useSyncQuests(sessionId)
  const gain = useSessionGain(sessionId)
  const sex = useBodySex()
  const muscle = useMuscleLabels()
  const [selected, setSelected] = useState<string | null>(null)

  // Ответ не того вида (старый сервер, заглушка) — карточку не показываем.
  const data = gain.data && typeof gain.data.muscles === 'object' ? gain.data : undefined
  // Итог новой тренировки — повод освежить персонажа (уровень, облачко маскота «+XP»).
  const client = useQueryClient()
  const loadedId = data?.id
  useEffect(() => {
    if (!loadedId) return
    void client.invalidateQueries({ queryKey: ['character'], exact: true })
    void client.invalidateQueries({ queryKey: ['character', 'achievements'] })
  }, [client, loadedId])
  const tones = useMemo(() => {
    const out: Record<string, MuscleTone> = {}
    for (const key of Object.keys(FIGURE_MUSCLES)) out[key] = data?.muscles[key]?.role ?? 'idle'
    return out
  }, [data])

  if (!data) return null

  const worked = Object.entries(data.muscles).sort(
    ([, a], [, b]) => Number(b.role === 'primary') - Number(a.role === 'primary') || b.sets - a.sets,
  )
  const levelUp = data.level > data.level_before
  const span = data.next_level_xp - data.level_xp
  const share = (xp: number) => `${Math.max(0, Math.min(1, (xp - data.level_xp) / span)) * 100}%`
  const work = selected ? data.muscles[selected] : undefined

  return (
    <Card
      title="Итог для персонажа"
      className={className}
      actions={
        <Link to="/character" className="text-sm text-muted hover:text-accent">
          Персонаж →
        </Link>
      }
    >
      {levelUp && celebrate ? <LevelCelebration key={sessionId} sessionId={sessionId} level={data.level} /> : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-3xl font-bold text-accent">+{formatNumber(data.xp, { digits: 0 })} XP</span>
        {levelUp ? (
          <Badge tone="accent">
            Новый уровень {data.level}
            {data.level - data.level_before > 1 ? ` (+${data.level - data.level_before})` : ''}!
          </Badge>
        ) : (
          <span className="text-sm text-muted">
            Уровень {data.level} · {data.title_after}
          </span>
        )}
      </div>
      {data.bonus_xp ? <p className="mt-2 text-sm text-accent">В том числе +{data.bonus_xp} XP за задания недели</p> : null}
      <div className="mt-3">
        <div
          className="relative h-2 overflow-hidden rounded-full bg-surface-2"
          role="progressbar"
          aria-label={`Опыт уровня ${data.level}`}
          aria-valuemin={data.level_xp}
          aria-valuemax={data.next_level_xp}
          aria-valuenow={data.xp_after}
        >
          <div className="absolute inset-y-0 left-0 rounded-full bg-accent" style={{ width: share(data.xp_after) }} />
          {!levelUp ? (
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-accent-strong opacity-60"
              style={{ width: share(data.xp_before) }}
            />
          ) : null}
        </div>
        <p className="mt-1 text-xs text-muted">
          {formatNumber(data.xp_after, { digits: 0 })} / {formatNumber(data.next_level_xp, { digits: 0 })} XP до уровня{' '}
          {data.level + 1} · 50 за тренировку, 5 за рабочий подход и бонусы за задания
        </p>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-start">
        <div>
          <MuscleMap
            sex={sex}
            tones={tones}
            selected={selected}
            onSelect={setSelected}
            label={`Работали: ${worked.map(([k]) => muscle(k)).join(', ') || 'ничего'}`}
            className="mx-auto w-full max-w-sm"
          />
          <MuscleLegend tones={['primary', 'secondary', 'idle']} className="mt-2 justify-center" />
        </div>
        <div className="flex flex-col gap-2">
          <p className="min-h-10 rounded-lg bg-surface-2 px-3 py-2 text-sm" aria-live="polite">
            {selected ? (
              <>
                <span className="font-medium">{muscle(selected)}</span> ·{' '}
                {work
                  ? `${work.role === 'primary' ? 'основная' : 'вспомогательная'}, ${work.sets} ${pluralRu(work.sets, SETS)}`
                  : 'в тренировке не работала'}
              </>
            ) : (
              <span className="text-muted">Нажми на мышцу, чтобы увидеть подробности</span>
            )}
          </p>
          {worked.length ? (
            <ul aria-label="Мышцы тренировки" className="flex flex-col">
              {worked.map(([key, w]) => (
                <li key={key} className="flex items-center gap-2 border-b border-border py-1.5 text-sm last:border-0">
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: `var(--muscle-${w.role})` }}
                  />
                  <span className={cn('flex-1', w.role === 'primary' ? 'font-medium' : 'text-muted')}>{muscle(key)}</span>
                  <span className="text-muted">
                    {w.sets} {pluralRu(w.sets, SETS)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">Рабочих подходов нет — опыт только за тренировку.</p>
          )}
        </div>
      </div>
    </Card>
  )
}
